import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { Slider } from '@/components/ui/slider';
import { CustomSelect } from '@/components/CustomSelect';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, Cell, ZAxis } from 'recharts';
import { Target, ArrowUpRight, ArrowDownRight, Zap, AlertTriangle, TrendingDown, Shield, DollarSign } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const tip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';
function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }

function Delta({ v, suffix = '', invert = false }) {
  if (!v || Math.abs(v) < 0.01) return null;
  const pos = invert ? v < 0 : v > 0;
  return <span className={`text-[11px] font-sans font-semibold ${pos ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>{v > 0 ? '+' : ''}{Math.abs(v) >= 100 ? fmt(v) : v.toFixed(1)}{suffix}</span>;
}

export default function StrategyMonitor() {
  const { assets: ctxAssets, loading: pfLoading } = usePortfolio();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('base');
  // Budget allocation sim
  const [budgetAction, setBudgetAction] = useState('acquire');
  const [budgetAmount, setBudgetAmount] = useState(500000);
  const [acqYield, setAcqYield] = useState(5.0);
  const [acqType, setAcqType] = useState('commercial');

  useEffect(() => {
    if (pfLoading) return;
    setAssets(ctxAssets);
    setLoading(false);
  }, [pfLoading, ctxAssets]);

  const intel = useMemo(() => {
    if (!assets.length) return null;
    const tv = assets.reduce((s, a) => s + (a.metrics?.market_value || 0), 0);
    const td = assets.reduce((s, a) => s + (a.metrics?.loan_amount || 0), 0);
    const tNoi = assets.reduce((s, a) => s + (a.metrics?.noi || 0), 0);
    const tCf = assets.reduce((s, a) => s + (a.metrics?.net_cashflow || 0), 0);
    const tAds = assets.reduce((s, a) => s + (a.metrics?.annual_debt_service || 0), 0);
    const ltv = tv > 0 ? td / tv * 100 : 0;
    const dscr = tAds > 0 ? tNoi / tAds : 0;
    const avgYield = tv > 0 ? tNoi / tv * 100 : 0;
    const avgVac = assets.reduce((s, a) => s + (a.vacancy_rate || 0), 0) / assets.length;
    const pirr = (() => { const tw = assets.reduce((s, a) => s + Math.max((a.metrics?.equity || 0), 1), 0); return assets.reduce((s, a) => s + (a.metrics?.irr || 0) * Math.max((a.metrics?.equity || 0), 1) / tw, 0); })();

    // Segments
    const segs = {}; assets.forEach(a => { const t = a.property_type || 'residential'; segs[t] = (segs[t] || 0) + (a.annual_rent || 0); });
    const totalRent = assets.reduce((s, a) => s + (a.annual_rent || 0), 0);
    const segPcts = Object.entries(segs).map(([k, v]) => ({ name: k, pct: totalRent > 0 ? v / totalRent * 100 : 0 }));
    const resPct = segPcts.find(s => s.name === 'residential')?.pct || 0;

    // Cities
    const cityAlloc = {}; assets.forEach(a => { const c = a.city || '?'; cityAlloc[c] = (cityAlloc[c] || 0) + (a.metrics?.market_value || 0); });
    const cityData = Object.entries(cityAlloc).map(([k, v]) => ({ name: k, pct: tv > 0 ? +(v / tv * 100).toFixed(1) : 0 })).sort((a, b) => b.pct - a.pct);

    // Targets & deviations
    const targets = [
      { label: 'Asset Mix (Residential)', current: resPct, target: [50, 60], unit: '%', key: 'mix' },
      { label: 'Avg Net Yield', current: avgYield, target: [4.5, 5.5], unit: '%', key: 'yield' },
      { label: 'Portfolio IRR', current: pirr, target: [12, 999], unit: '%', key: 'irr' },
      { label: 'Weighted LTV', current: ltv, target: [0, 65], unit: '%', key: 'ltv', invert: true },
      { label: 'Vacancy Rate', current: avgVac, target: [0, 3], unit: '%', key: 'vac', invert: true },
      { label: 'DSCR', current: dscr, target: [1.2, 999], unit: 'x', key: 'dscr' },
    ].map(t => {
      const inRange = t.current >= t.target[0] && t.current <= t.target[1];
      const deviation = !inRange ? (t.current < t.target[0] ? t.target[0] - t.current : t.current - t.target[1]) : 0;
      return { ...t, inRange, deviation: +deviation.toFixed(2) };
    });

    // Strategy Score
    const mixPts = resPct >= 50 && resPct <= 60 ? 20 : resPct >= 45 && resPct <= 65 ? 14 : 6;
    const yieldPts = avgYield >= 4.5 ? 18 : avgYield >= 4 ? 12 : 5;
    const irrPts = pirr >= 12 ? 18 : pirr >= 10 ? 12 : 5;
    const ltvPts = ltv <= 60 ? 16 : ltv <= 65 ? 10 : 4;
    const vacPts = avgVac <= 3 ? 14 : avgVac <= 5 ? 8 : 3;
    const dscrPts = dscr >= 1.25 ? 14 : dscr >= 1.15 ? 8 : 3;
    const strategyScore = mixPts + yieldPts + irrPts + ltvPts + vacPts + dscrPts;
    const onTarget = targets.filter(t => t.inRange).length;

    // Scatter data for matrix
    const scatterData = assets.map(a => {
      const m = a.metrics || {};
      const risk = 100 - Math.min(100, Math.round(((m.dscr || 0) - 1) * 40 + (100 - (m.ltv || 60)) * 0.8));
      const ret = m.irr || 0;
      const quadrant = risk < 50 && ret >= 12 ? 'Core' : risk < 50 ? 'Optimize' : ret >= 12 ? 'Reduce Risk' : 'Divest';
      return { name: a.name, risk, ret, quadrant, mv: m.market_value || 0 };
    });

    // Budget sim impact
    let simLtv = ltv, simDscr = dscr, simIrr = pirr, simScore = strategyScore, simResPct = resPct;
    if (budgetAction === 'acquire') {
      const newMv = budgetAmount / 0.7;
      const newLa = budgetAmount;
      const newNoi = newMv * acqYield / 100;
      const newAds = mp(newLa, 3.5 / 100, 30) * 12;
      simLtv = (td + newLa) / (tv + newMv) * 100;
      simDscr = (tAds + newAds) > 0 ? (tNoi + newNoi) / (tAds + newAds) : 0;
      if (acqType !== 'residential') simResPct = resPct * (totalRent / (totalRent + newNoi));
    } else if (budgetAction === 'repay') {
      simLtv = tv > 0 ? (td - budgetAmount) / tv * 100 : 0;
      simDscr = dscr * 1.08;
    } else if (budgetAction === 'renovate') {
      simDscr = dscr * 1.03;
      simIrr = pirr + 0.5;
    }
    const simScoreDelta = Math.round((simLtv < ltv ? 3 : -2) + (simDscr > dscr ? 3 : -1) + (simResPct < resPct && simResPct >= 50 ? 4 : 0));

    // Scenarios
    const stressFactors = { base: 1, rent: 0.92, vacancy: 0.93, rate: 0.95, combined: 0.85 };
    const stressLabels = { base: 'Base', rent: 'Rent -8%', vacancy: 'Vacancy +5%', rate: 'Rate +1%', combined: 'Combined' };

    return {
      tv, td, tCf, ltv, dscr, avgYield, pirr, avgVac, resPct, totalRent,
      segPcts, cityData, targets, strategyScore, onTarget,
      mixPts, yieldPts, irrPts, ltvPts, vacPts, dscrPts,
      scatterData,
      simLtv, simDscr, simIrr, simResPct, simScoreDelta,
      stressFactors, stressLabels,
    };
  }, [assets, budgetAction, budgetAmount, acqYield, acqType]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!intel) return <div className="text-center py-20 text-[#8C877F]">No data.</div>;

  const stressFactor = intel.stressFactors[scenario] || 1;
  const stressDscr = intel.dscr * stressFactor;
  const stressIrr = intel.pirr * stressFactor;
  const stressScore = Math.round(intel.strategyScore * stressFactor);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="strategy-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Strategic Capital Command Center</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Strategy & Allocation</h1>
        </div>
        <div className="flex gap-1.5">
          {Object.entries(intel.stressLabels).map(([id, l]) => (
            <button key={id} onClick={() => setScenario(id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium transition-all ${scenario === id ? 'bg-[#1F3A34] text-white' : 'bg-[#F4F1EB] text-[#5A5A5A] hover:bg-[#ECE7DE]'}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Score + Breakdown + Improvement */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 flex flex-col items-center justify-center" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 120 120" className="w-full h-full">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#F0EDE8" strokeWidth="8" />
              <circle cx="60" cy="60" r="52" fill="none" stroke={scenario === 'base' ? (intel.strategyScore >= 70 ? '#2E6F57' : intel.strategyScore >= 50 ? '#A67C52' : '#8A3C3C') : (stressScore >= 70 ? '#2E6F57' : '#8A3C3C')} strokeWidth="8"
                strokeDasharray={`${2*Math.PI*52}`} strokeDashoffset={`${2*Math.PI*52*(1-(scenario === 'base' ? intel.strategyScore : stressScore)/100)}`}
                strokeLinecap="round" transform="rotate(-90 60 60)" className="transition-all duration-700" />
              <text x="60" y="56" textAnchor="middle" fill="#1C1C1C" fontSize="28" fontFamily="Inter, system-ui" fontWeight="600">{scenario === 'base' ? intel.strategyScore : stressScore}</text>
              <text x="60" y="72" textAnchor="middle" fill="#8C877F" fontSize="10">/100</text>
            </svg>
          </div>
          <p className="text-[14px] font-sans font-semibold text-[#1C1C1C] mt-2">Strategy Health</p>
          <p className="text-[12px] text-[#8C877F]">{intel.onTarget}/{intel.targets.length} metrics on target</p>
          {scenario !== 'base' && <p className="text-[11px] text-[#8A3C3C] font-sans font-medium mt-1">{intel.stressLabels[scenario]} scenario</p>}
        </div>

        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-2.5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Score Breakdown</p>
          {[
            { l: 'Asset Mix', pts: intel.mixPts, max: 20 },
            { l: 'Yield vs Target', pts: intel.yieldPts, max: 18 },
            { l: 'IRR vs Target', pts: intel.irrPts, max: 18 },
            { l: 'LTV Stability', pts: intel.ltvPts, max: 16 },
            { l: 'Vacancy', pts: intel.vacPts, max: 14 },
            { l: 'Debt Coverage', pts: intel.dscrPts, max: 14 },
          ].map(s => (
            <div key={s.l} className="flex items-center gap-3">
              <span className="text-[11px] text-[#5A5A5A] font-sans w-28">{s.l}</span>
              <div className="flex-1 h-2 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full transition-all" style={{ width: `${s.pts/s.max*100}%`, backgroundColor: s.pts/s.max >= 0.7 ? '#2E6F57' : s.pts/s.max >= 0.5 ? '#A67C52' : '#8A3C3C' }} /></div>
              <span className="text-[11px] font-sans font-semibold w-10 text-right">{s.pts}/{s.max}</span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-2.5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Improvement Path</p>
          {[
            { a: `Residential → 58%`, d: '+5', c: intel.resPct > 60 },
            { a: `Net Yield → 4.8%`, d: '+6', c: intel.avgYield < 4.5 },
            { a: `LTV → 57%`, d: '+4', c: intel.ltv > 60 },
            { a: `DSCR → 1.30x`, d: '+3', c: intel.dscr < 1.3 },
          ].filter(s => s.c).slice(0, 4).map(s => (
            <div key={s.a} className="flex items-center justify-between py-1.5 border-b border-[#F4F1EB] last:border-0">
              <span className="text-[12px] text-[#5A5A5A] font-sans">{s.a}</span>
              <span className="text-[11px] text-[#2E6F57] bg-[#E6EFEA] px-2 py-0.5 rounded font-sans font-medium">{s.d}</span>
            </div>
          ))}
          {intel.targets.filter(t => t.inRange).length === intel.targets.length && <p className="text-[12px] text-[#2E6F57] font-sans">All targets met. Portfolio well-positioned.</p>}
        </div>
      </div>

      {/* Target Deviations */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="grid grid-cols-6 gap-1 px-5 py-3 border-b border-[#E2DDD3] text-[10px] text-[#8C877F] font-sans font-medium uppercase tracking-wider">
          <span className="col-span-2">Metric</span><span className="text-right">Current</span><span className="text-center">Target Range</span><span className="text-right">Deviation</span><span className="text-center">Status</span>
        </div>
        {intel.targets.map(t => (
          <div key={t.key} className={`grid grid-cols-6 gap-1 px-5 py-3.5 border-b border-[#F4F1EB] last:border-0 text-[13px] ${!t.inRange ? 'bg-[#FDF8F0]/30' : ''}`}>
            <span className="col-span-2 font-sans font-medium text-[#1C1C1C]">{t.label}</span>
            <span className="text-right font-sans font-semibold text-[#1C1C1C]">{t.current.toFixed(1)}{t.unit}</span>
            <span className="text-center font-sans text-[#8C877F]">{t.target[0]}{t.target[1] < 100 ? `–${t.target[1]}` : '+'}{t.unit}</span>
            <span className="text-right font-sans font-semibold" style={{ color: t.inRange ? '#2E6F57' : '#8A3C3C' }}>{t.inRange ? '—' : `${t.deviation.toFixed(1)}${t.unit}`}</span>
            <div className="flex justify-center"><span className="text-[10px] px-2 py-0.5 rounded-lg font-sans font-medium" style={{ backgroundColor: t.inRange ? '#E6EFEA' : '#FDF8F0', color: t.inRange ? '#2E6F57' : '#A67C52' }}>{t.inRange ? 'On Target' : 'Off Target'}</span></div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Allocation */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Geographic Allocation</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={intel.cityData} layout="vertical">
              <CartesianGrid stroke="#E3DED6" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#5A5A5A', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={tip} formatter={v => [`${v}%`, 'Allocation']} />
              <Bar dataKey="pct" barSize={16} radius={[0, 4, 4, 0]}>
                {intel.cityData.map((c, i) => <Cell key={i} fill={c.pct > 50 ? '#8A3C3C' : c.pct > 30 ? '#A67C52' : '#1F3A34'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Opportunity vs Risk Matrix */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Opportunity vs Risk Matrix</p>
          <ResponsiveContainer width="100%" height={180}>
            <ScatterChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid stroke="#E3DED6" />
              <XAxis type="number" dataKey="risk" name="Risk" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} label={{ value: 'Risk →', position: 'bottom', fill: '#8C877F', fontSize: 10 }} />
              <YAxis type="number" dataKey="ret" name="Return" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 25]} label={{ value: 'Return →', angle: -90, position: 'left', fill: '#8C877F', fontSize: 10 }} />
              <ZAxis range={[40, 200]} />
              <Tooltip contentStyle={tip} formatter={(v, n) => [typeof v === 'number' ? v.toFixed(1) : v, n]} />
              <Scatter data={intel.scatterData}>
                {intel.scatterData.map((d, i) => <Cell key={i} fill={d.quadrant === 'Core' ? '#2E6F57' : d.quadrant === 'Optimize' ? '#1F3A34' : d.quadrant === 'Reduce Risk' ? '#A67C52' : '#8A3C3C'} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-2 justify-center">
            {[{ l: 'Core', c: '#2E6F57' }, { l: 'Optimize', c: '#1F3A34' }, { l: 'Reduce Risk', c: '#A67C52' }, { l: 'Divest', c: '#8A3C3C' }].map(q => (
              <div key={q.l} className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: q.c }} /><span className="text-[10px] text-[#8C877F]">{q.l}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Capital Allocation Simulator */}
      <div className="bg-white border border-[#1F3A34]/15 rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(31,58,52,0.05)' }}>
        <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Capital Allocation Simulator — Available Budget: {fmt(850000)}</p></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Action</label>
            <CustomSelect value={budgetAction} onChange={setBudgetAction} options={[
              { value: 'acquire', label: 'New Acquisition', sub: 'Buy asset' },
              { value: 'repay', label: 'Debt Reduction', sub: 'Pay down loans' },
              { value: 'renovate', label: 'Renovation', sub: 'Value-add investment' },
            ]} />
          </div>
          <div>
            <label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Amount (EUR)</label>
            <Slider value={[budgetAmount]} onValueChange={v => setBudgetAmount(v[0])} min={100000} max={850000} step={50000}
              className="mt-3 [&_[role=slider]]:bg-[#1F3A34] [&_[role=slider]]:border-0 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3" />
            <p className="text-[13px] font-sans font-semibold text-[#1F3A34] mt-1">{fmt(budgetAmount)}</p>
          </div>
          {budgetAction === 'acquire' && <>
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Target Yield %</label><input type="number" step="0.1" value={acqYield} onChange={e => setAcqYield(parseFloat(e.target.value) || 0)} className="w-full bg-white border border-[#E2DDD3] rounded-lg px-3 py-2.5 text-[13px] font-sans focus:border-[#1F3A34] focus:outline-none" /></div>
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Type</label><CustomSelect value={acqType} onChange={setAcqType} options={[{ value: 'residential', label: 'Residential' }, { value: 'commercial', label: 'Commercial' }, { value: 'mixed', label: 'Mixed-Use' }]} /></div>
          </>}
        </div>
        {/* Impact */}
        <div className="grid grid-cols-5 gap-4 pt-4 border-t border-[#E2DDD3]">
          {[
            { l: 'LTV', base: intel.ltv, sim: intel.simLtv, suffix: '%', inv: true },
            { l: 'DSCR', base: intel.dscr, sim: intel.simDscr },
            { l: 'Residential Mix', base: intel.resPct, sim: intel.simResPct, suffix: '%', inv: true },
            { l: 'IRR', base: intel.pirr, sim: intel.simIrr, suffix: '%' },
            { l: 'Strategy Score', base: intel.strategyScore, sim: intel.strategyScore + intel.simScoreDelta, suffix: '' },
          ].map(k => (
            <div key={k.l}>
              <p className="text-[10px] text-[#8C877F]">{k.l}</p>
              <p className="text-[16px] font-sans font-semibold text-[#1C1C1C]">{k.sim.toFixed(1)}{k.suffix}</p>
              <Delta v={k.sim - k.base} suffix={k.suffix} invert={k.inv} />
            </div>
          ))}
        </div>
      </div>

      {/* AI Strategic Recommendations */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-semibold">AI Strategic Recommendations</p></div>
        {intel.resPct > 60 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Deploy <strong>{fmt(budgetAmount)}</strong> into <strong>commercial</strong> asset at {acqYield}% yield → Residential mix drops to <strong className="text-[#2E6F57]">{intel.simResPct.toFixed(0)}%</strong>, Strategy Score <strong className="text-[#2E6F57]">+{intel.simScoreDelta}</strong>.
        </p>}
        {intel.ltv > 58 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Use <strong>{fmt(500000)}</strong> for debt reduction → LTV drops to <strong className="text-[#2E6F57]">{(intel.ltv - 500000 / intel.tv * 100).toFixed(1)}%</strong> → Risk Score improves, Bank Readiness increases.
        </p>}
        {intel.avgYield < 4.5 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Sell lowest-yield asset and redeploy into higher-yielding opportunity → Avg yield moves toward <strong className="text-[#2E6F57]">4.8% target</strong>.
        </p>}
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Optimal allocation: <strong>{fmt(Math.round(budgetAmount * 0.6))}</strong> acquisition + <strong>{fmt(Math.round(budgetAmount * 0.4))}</strong> debt reduction maximizes risk-adjusted return.
        </p>
      </div>

      {/* Alerts */}
      {intel.targets.filter(t => !t.inRange).length > 0 && (
        <div className="bg-white border border-[#A67C52]/20 rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#A67C52]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Strategic Alerts</p></div>
          {intel.targets.filter(t => !t.inRange).map(t => (
            <div key={t.key} className="flex items-center gap-3 py-2 border-b border-[#F4F1EB] last:border-0">
              <div className="w-2 h-2 rounded-full bg-[#A67C52]" />
              <p className="text-[12px] text-[#1C1C1C] font-sans"><strong>{t.label}</strong> at {t.current.toFixed(1)}{t.unit} — {t.deviation.toFixed(1)}{t.unit} outside target range ({t.target[0]}–{t.target[1] < 100 ? t.target[1] : '∞'}{t.unit})</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
